import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import rolesService from "../../services/rolesService";
import toast from "react-hot-toast";
import { DeleteModal } from "../../components";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Trash,
  PencilSimple,
  WarningCircle,
  Spinner,
  MagnifyingGlass,
  ArrowRight,
  ArrowsClockwise,
  Shield,
  X,
} from "phosphor-react";
import { useAccessChecker } from "../../utils/Functions/Authorization";

/* -------------------------------------------------------
   Roles (theme-aligned + phosphor icons)
   - SAME LOGIC as your old component
------------------------------------------------------- */
function Roles() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState(null);
  const [done, setDone] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [filters, setFilters] = useState({ searchValue: "" });
  const { checkAccess } = useAccessChecker();

  useEffect(() => {
    getRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const getRoles = async () => {
    try {
      setLoading(true);
      const response = await rolesService.search(filters);
      const responseData = response.data;
      if (responseData?.statusCode === 200) setRoles(responseData.data || []);
      else setRoles([]);
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Failed to fetch roles"));
      console.error("Role fetch failed:", error?.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value) => setFilters({ searchValue: value });
  const handleSearch = () => getRoles();

  const reset = (e) => {
    e.preventDefault();
    setFilters({ searchValue: "" });
    setTimeout(() => getRoles(), 0);
  };

  const filtersActive = useMemo(() => String(filters.searchValue || "").trim() !== "", [
    filters.searchValue,
  ]);

  return (
    <div className="lg:p-7 p-4 max-w-full mx-auto">
      {/* Header */}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>

          <input
            type="text"
            placeholder={t("Search roles...")}
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950 placeholder-neutral-500 text-neutral-800 dark:text-neutral-200 focus:ring-1 ring-neutral-300 dark:ring-neutral-700 focus:outline-none"
            value={filters.searchValue}
            onChange={(e) => handleFilterChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />

          <button
            onClick={handleSearch}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            aria-label={t("Submit search")}
          >
            <ArrowRight size={16} />
          </button>


        </div>

        {/* Reset */}
        <button
          onClick={reset}
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          aria-label={t("Reset")}
        >
          <ArrowsClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">{t("Reset")}</span>
        </button>


        {checkAccess("roles", "read") && (
          <Link
            to="/settings/roles/new"
            className="flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-[1.02] transition"
          >
            <Plus size={16} />
            <span className="text-sm">{t("Add Role")}</span>
          </Link>
        )}
      </div>

      {/* Active filter chip */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {t("Active filters")}:
          </span>

          <Chip
            icon={<MagnifyingGlass size={12} />}
            label={filters.searchValue}
            onClear={() => {
              setFilters({ searchValue: "" });
              setTimeout(() => getRoles(), 0);
            }}
          />

          <button
            onClick={reset}
            className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>{t("Clear all")}</span>
          </button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size={28} className="animate-spin text-neutral-600 dark:text-neutral-400" />
        </div>
      ) : roles.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {roles.map((role, i) => (
            <div
              key={role._id}
              className={cx(
                "relative rounded-xl border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-900/30  transition-all overflow-hidden",
                "p-5"
              )}
            >

              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-xl border border-neutral-150 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/40">
                      <Shield size={16} className="text-neutral-700 dark:text-neutral-200" />
                    </span>

                    <h3 className="text-sm font-[500] text-neutral-900 dark:text-white truncate">
                      {t(role.name)}
                    </h3>
                  </div>

                  <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3">
                    {role.description || t("No description provided")}
                  </p>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {checkAccess("roles", "update") && (
                    <Link
                      to={`/settings/roles/update/${role._id}`}
                      title={t("Edit")}
                      className="p-2 rounded-full border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-green-600 dark:text-green-500 transition-colors"
                    >
                      <PencilSimple size={16} />
                    </Link>
                  )}

                  {checkAccess("roles", "delete") && (
                    <button
                      onClick={() => {
                        setRoleId(role._id);
                        setDeleteModal(true);
                      }}
                      title={t("Delete")}
                      className="p-2 rounded-full border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-red-600 dark:text-red-400 transition-colors"
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              </div>

            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 bg-white dark:bg-neutral-950 p-6 rounded-xl border border-neutral-150 dark:border-neutral-900">
          <div className="h-16 w-16 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
            <WarningCircle size={28} className="text-neutral-600 dark:text-neutral-400" />
          </div>

          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-1">
            {t("No Roles Found")}
          </h3>

          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md">
            {filters.searchValue
              ? t("No roles match your search criteria.")
              : t("No data to show.")}
          </p>

          {checkAccess("roles", "read") && (
            <Link
              to="/settings/roles/new"
              className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-neutral-900 dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-[1.02] transition"
            >
              <Plus size={16} className="mr-2" />
              {t("Add Your First Role")}
            </Link>
          )}
        </div>
      )}

      {/* Delete Modal (same logic) */}
      <DeleteModal
        setDeleteId={setRoleId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={roleId}
        Model="role"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  );
}

export default Roles;

/* -------------------- helpers -------------------- */
function Chip({ icon, label, onClear }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
      {icon}
      <span className="max-w-[240px] truncate">{label}</span>
      <button
        type="button"
        onClick={onClear}
        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
        aria-label={t("clear")}
      >
        <X size={12} />
      </button>
    </div>
  );
}

function cx() {
  return Array.from(arguments).filter(Boolean).join(" ");
}